// src/pages/designer/DesignerDashboard.jsx
import React, { useEffect, useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { getOrders, getSettings, tierPlainText, commissionRateForDesignerProfile, nextAnnualResetDate } from "../../lib/db";
import { StatCard, TableCard, Table, Badge, SectionLabel, Spinner, ProgressBar, fmt$, fmtDate, orderStatusForBadge, CommissionPctFigures } from "../../components/UI";

export default function DesignerDashboard() {
  const pagePad = "var(--page-pad-x)";
  const { profile } = useAuth();
  const [orders, setOrders]     = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!profile?.uid) return undefined;
    let cancelled = false;
    setLoadError("");
    setLoading(true);
    Promise.all([getOrders(profile.uid), getSettings()])
      .then(([o, s]) => {
        if (!cancelled) {
          setOrders(o);
          setSettings(s);
        }
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) {
          setLoadError(
            "We couldn’t load your dashboard (network or Firestore permissions). Try refreshing; if it persists, contact support."
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [profile?.uid]);

  if (!profile) return <Spinner />;

  if (loading && !loadError) return <Spinner />;

  if (loadError || !settings) {
    return (
      <div style={{ padding: 48, maxWidth: 560 }}>
        <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 400 }}>Dashboard</h2>
        <p style={{ fontSize: 14, color: "var(--mid)", marginTop: 14, lineHeight: 1.6 }}>
          {loadError || "Unable to load settings."}
        </p>
      </div>
    );
  }

  const tier        = tierPlainText(profile.tier);
  const effectiveRate = commissionRateForDesignerProfile(profile, settings);
  const rateDisplay =
    effectiveRate == null
      ? "Pending"
      : <CommissionPctFigures rate={effectiveRate} color="var(--gold)" fontSize={38} />;
  const ytdSales    = profile.ytdSales || 0;
  const ytdEarned   = profile.ytdEarned || 0;
  const pending     = profile.pendingPayout || 0;
  const paid        = profile.totalPaid || 0;

  const tiers       = settings.tiers || [{ name:"Partner",rate:8,minSales:0,maxSales:49999 },{ name:"Preferred",rate:10,minSales:50000,maxSales:99999 },{ name:"Principal",rate:12,minSales:100000,maxSales:null }];
  const currentIdx  = tiers.findIndex(t => t.name === tier);
  const nextTier    = tiers[currentIdx + 1];
  const toNext      = nextTier ? Math.max(0, nextTier.minSales - ytdSales) : 0;
  const progressMax = nextTier ? nextTier.minSales : ytdSales || 1;
  const progressPct = nextTier ? Math.min(100, Math.round((ytdSales / progressMax) * 100)) : 100;

  const recentOrders = orders.slice(0, 5);

  const cols = [
    { key:"num", label:"Sales Order #" },
    { key:"projectId", label:"Project ID" },
    { key:"attach", label:"File" },
    { key:"client", label:"Client / Project" },
    { key:"amount", label:"Amount" },
    { key:"comm", label:"Commission" },
    { key:"statusEl", label:"Status" },
    { key:"date", label:"Date" },
  ];

  const rows = recentOrders.map(o => ({
    num: <span style={{fontFamily:"'Cormorant Garamond',serif",fontWeight:400}}>{o.orderNumber || o.id.slice(0,8).toUpperCase()}</span>,
    projectId: o.projectId || "—",
    attach: o.attachmentUrl ? (
      <a href={o.attachmentUrl} target="_blank" rel="noopener noreferrer" style={{ color: "var(--gold)", fontWeight: 500 }}>
        {o.attachmentName || "Open"}
      </a>
    ) : "—",
    client: o.clientName,
    amount: fmt$(o.amount),
    comm: <span style={{color:"var(--gold)"}}>{fmt$(o.commission)}</span>,
    statusEl: <Badge label={orderStatusForBadge(o.status)} />,
    date: fmtDate(o.createdAt),
  }));

  return (
    <div>
      {/* Hero */}
      <div style={{ background:"var(--charcoal)", padding:`30px ${pagePad}`, color:"var(--cream)", marginBottom:0, position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", right:-60, top:-60, width:280, height:280, borderRadius:"50%", border:"1px solid rgba(184,150,90,0.2)" }} />
        <div style={{ position:"absolute", right:100, bottom:-100, width:200, height:200, borderRadius:"50%", border:"1px solid rgba(184,150,90,0.1)" }} />
        <div style={{ position:"relative", zIndex:1 }}>
          <div style={{ fontSize:12, letterSpacing:"0.15em", textTransform:"uppercase", color:"rgba(245,240,232,0.5)", marginBottom:6 }}>Welcome back</div>
          <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:32, fontWeight:300, fontStyle:"italic", marginBottom:16 }}>{profile.name}</h2>
          <div style={{ display:"flex", gap:48, flexWrap:"wrap" }}>
            <div>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:52, fontWeight:300, color:"var(--gold-light)", lineHeight:1 }}>{fmt$(ytdEarned)}</div>
              <div style={{ fontSize:10, letterSpacing:"0.15em", textTransform:"uppercase", color:"rgba(245,240,232,0.45)", marginTop:4 }}>Total Earned This Year</div>
            </div>
            <div style={{ display:"flex", gap:40, alignItems:"flex-end", flexWrap:"wrap" }}>
              {[["Pending Payout", fmt$(pending)], ["Paid Out", fmt$(paid)], ["Orders", orders.length]].map(([l,v]) => (
                <div key={l}>
                  <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:26, fontWeight:300 }}>{v}</div>
                  <div style={{ fontSize:10, color:"rgba(245,240,232,0.4)", letterSpacing:"0.12em", textTransform:"uppercase" }}>{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding:`22px ${pagePad} 36px` }}>
        <div style={{ fontSize:12, color:"var(--mid)", marginBottom:18, padding:"12px 16px", background:"var(--warm-white)", border:"1px solid var(--border)", borderRadius:4 }}>
          Program-year totals reset on <strong>{fmtDate(nextAnnualResetDate(profile))}</strong> (each anniversary rolls forward ~365 days; tiers restart from Program Settings thresholds).
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:16, marginBottom:28 }}>
          <StatCard label="YTD Sales Referred" value={fmt$(ytdSales)} />
          <StatCard
            label="Commission Rate"
            value={rateDisplay}
            accent={effectiveRate != null}
            hint={effectiveRate == null ? "Showroom will confirm your rate shortly." : undefined}
            valueStyle={effectiveRate != null ? { fontFamily: "'Jost', sans-serif", fontWeight: 500 } : undefined}
          />
          <StatCard label="Current Tier" value={tier} />
          <div style={{ background:"var(--warm-white)", border:"1px solid var(--border)", borderRadius:4, padding:"22px 20px" }}>
            <div style={{ fontSize:10, fontWeight:500, letterSpacing:"0.18em", textTransform:"uppercase", color:"var(--mid)", marginBottom:10 }}>
              {nextTier ? `To ${nextTier.name} Tier` : "Top Tier! 🎉"}
            </div>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:30, fontWeight:300, color:"var(--charcoal)", lineHeight:1 }}>
              {nextTier ? fmt$(toNext) : "—"}
            </div>
            <ProgressBar
              value={progressPct}
              max={100}
              label={
                nextTier
                  ? `${progressPct}% toward ${nextTier.name} (${nextTier.rate}% commission)`
                  : "Highest tier achieved"
              }
            />
          </div>
        </div>

        <SectionLabel>Recent Orders</SectionLabel>
        <TableCard title="Latest Activity">
          <Table cols={cols} rows={rows} emptyMsg="No orders yet — submit your first referral!" />
        </TableCard>
      </div>
    </div>
  );
}
